import { useEffect, useRef, useState } from "react";
import { LockIcon as LockIcon, ShieldAlert, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLock } from "@/context/LockContext";
import ProfilePhoto from "@/components/ui/ProfilePhoto";
import { Button } from "@/components/ui/button";

/**
 * LockScreen — full-viewport overlay shown when `LockContext.isLocked === true`.
 *
 * Layout (spec 2026-02-18)
 * ------------------------
 *   • Blur backdrop over the current app view (backdrop-filter: blur(24px))
 *   • Centered card: user avatar + first name + "Session verrouillée"
 *   • 4-digit PIN pad (individual boxes, auto-advance, auto-submit on 4th)
 *   • Shake animation on failure
 *   • 30s countdown after 3 failed attempts (server-enforced 429)
 *   • Escape hatch : "Me déconnecter" (calls /auth/logout, wipes token)
 */
export default function LockScreen() {
  const { user, logout } = useAuth();
  const { unlock } = useLock();
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const [shakeKey, setShakeKey] = useState(0);
  const inputsRef = useRef([]);
  const busyRef = useRef(false);

  // Focus first input on mount / after a failed try.
  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  // Cooldown ticker (decrements every second while > 0).
  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const id = setInterval(() => setCooldownLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldownLeft]);

  const submit = async (pin) => {
    if (busyRef.current || cooldownLeft > 0) return;
    busyRef.current = true;
    setError("");
    const res = await unlock(pin);
    busyRef.current = false;
    if (res.ok) return;   // overlay unmounts via context state flip
    if (res.cooldown) {
      // Parse remaining seconds from message ("…patientez N seconde(s)").
      const m = /(\d+)/.exec(res.message || "");
      setCooldownLeft(m ? Number(m[1]) : 30);
      setError(res.message || "Trop de tentatives.");
    } else if (typeof res.remaining === "number") {
      setError(res.remaining > 0
        ? `PIN invalide — ${res.remaining} tentative(s) restante(s).`
        : "PIN invalide.");
      if (res.remaining === 0) {
        setCooldownLeft(30);   // safety fallback
      }
    } else {
      setError(res.error || "PIN invalide.");
    }
    setDigits(["", "", "", ""]);
    setShakeKey((k) => k + 1);
    setTimeout(() => inputsRef.current[0]?.focus(), 60);
  };

  const setDigit = (idx, v) => {
    // Accept only a single digit.
    const clean = (v || "").replace(/\D/g, "").slice(-1);
    setDigits((d) => {
      const next = [...d];
      next[idx] = clean;
      // Auto-advance
      if (clean && idx < 3) {
        setTimeout(() => inputsRef.current[idx + 1]?.focus(), 0);
      }
      // Auto-submit when complete
      if (clean && idx === 3) {
        const full = next.join("");
        if (full.length === 4) setTimeout(() => submit(full), 30);
      }
      return next;
    });
    setError("");
  };

  const onKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === "Enter" && digits.every((d) => d)) {
      submit(digits.join(""));
    }
  };

  const onPaste = (e) => {
    const txt = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 4);
    if (txt.length === 4) {
      e.preventDefault();
      const arr = txt.split("");
      setDigits(arr);
      setTimeout(() => submit(txt), 30);
    }
  };

  const handleLogout = async () => {
    // Full sign-out — dumps the token and the lock state along with it.
    try { sessionStorage.removeItem("ledesk_lock_active"); } catch {}
    await logout();
    window.location.href = "/connexion";
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{
        zIndex: 9999,
        background: "rgba(20,20,20,0.65)",
        backdropFilter: "blur(24px) saturate(140%)",
        WebkitBackdropFilter: "blur(24px) saturate(140%)",
      }}
      data-testid="lock-screen"
    >
      <div
        key={shakeKey}
        className="relative w-[380px] max-w-[92vw] p-8 rounded-2xl text-center"
        style={{
          background: "rgba(28,28,28,0.94)",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 30px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.05) inset",
          animation: shakeKey > 0 && error ? "lockShake 0.42s ease-in-out" : "none",
        }}
      >
        {/* Small padlock icon at the top */}
        <div className="flex items-center justify-center gap-2 mb-4 text-vertec-blue">
          <LockIcon size={16} strokeWidth={2.4} />
          <span className="text-[11px] font-semibold uppercase tracking-widest">Session verrouillée</span>
        </div>

        <div className="flex flex-col items-center gap-2 mb-6">
          {/* iter409 — 3e endroit autorisé pour la photo de l'utilisateur lui-même (initiales si absente, 72 px inchangé) */}
          <ProfilePhoto user={user} size={72} testid="lock-avatar" />
          <div className="text-lg font-semibold mt-2">Bonjour {user?.first_name || ""}</div>
          <div className="text-xs text-muted-foreground">Entrez votre code PIN pour reprendre.</div>
        </div>

        <div className="flex justify-center gap-3 mb-3" onPaste={onPaste} data-testid="lock-pin-pad">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputsRef.current[i] = el)}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              disabled={cooldownLeft > 0}
              className="w-12 h-14 text-center text-2xl font-bold rounded-lg outline-none transition-colors"
              style={{
                background: "#181818",
                border: `1px solid ${error ? "#EF4444" : "#333"}`,
                color: "#FFF",
                caretColor: "#0078D4",
              }}
              autoFocus={i === 0}
              data-testid={`lock-pin-digit-${i}`}
            />
          ))}
        </div>

        {cooldownLeft > 0 ? (
          <div
            className="flex items-center justify-center gap-1.5 text-[13px] text-status-danger mt-2"
            data-testid="lock-cooldown"
          >
            <ShieldAlert size={13} />
            <span>Blocage actif — {cooldownLeft}s avant nouvelle tentative</span>
          </div>
        ) : error ? (
          <div className="text-[13px] text-status-danger mt-2" data-testid="lock-error">{error}</div>
        ) : (
          <div className="text-[12px] text-muted-foreground mt-2">4 chiffres — le pavé se valide automatiquement.</div>
        )}

        <div className="mt-6 pt-4 border-t border-white/5 flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-muted-foreground hover:text-status-danger gap-1.5"
            data-testid="lock-logout-btn"
          >
            <LogOut size={13} /> Me déconnecter
          </Button>
        </div>
      </div>

      {/* Local keyframes — kept inline to avoid tailwind config churn. */}
      <style>{`
        @keyframes lockShake {
          0%,100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}
